import { useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { compressImage } from "../../lib/imageCompression";
import { openAuthFile } from "../../lib/fileOpen";
import AuthImage from "../AuthImage";
import { toast } from "sonner";
import { ShieldCheck, Wrench, CheckCircle, Camera, CircleNotch, ArrowUUpLeft } from "@phosphor-icons/react";

const when = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");

function StepRow({ step, i, busy, readOnly, onTick, onUndo, testid }) {
  return (
    <li className={`flex items-start gap-3 rounded-md px-3 py-3 min-h-[44px] ${step.done ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-muted/40"}`} data-testid={`${testid}-step-${i}`} data-done={step.done}>
      {step.done ? (
        <button type="button" onClick={() => step.photo && openAuthFile(step.photo.url)} className="shrink-0 h-14 w-14 rounded-md overflow-hidden border border-emerald-300 dark:border-emerald-800 bg-muted" title="Open the evidence photo" data-testid={`${testid}-step-${i}-photo`}>
          {step.photo ? <AuthImage path={step.photo.url} alt="Evidence" className="h-full w-full object-cover" /> : <CheckCircle className="h-6 w-6 m-auto text-emerald-600" weight="fill" />}
        </button>
      ) : (
        <button type="button" onClick={() => onTick(step)} disabled={readOnly || !!busy}
          className="shrink-0 h-14 w-14 rounded-md border-2 border-dashed border-red-400 text-red-600 flex flex-col items-center justify-center gap-0.5 hover:bg-red-50 dark:hover:bg-red-950/30 active:scale-95 transition-transform disabled:opacity-60"
          title="Take the evidence photo to confirm this step" data-testid={`${testid}-step-${i}-tick`}>
          {busy === step.key ? <CircleNotch className="h-6 w-6 animate-spin" /> : <><Camera className="h-6 w-6" /><span className="text-[9px] font-semibold uppercase">Photo</span></>}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${step.done ? "line-through text-muted-foreground" : "text-slate-800 dark:text-slate-200"}`}>{step.label || step.text}</p>
        {step.done ? (
          <p className="text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-2 flex-wrap">
            Confirmed {when(step.done_at)}{step.done_by ? ` · ${step.done_by}` : ""}
            {!readOnly && <button type="button" onClick={() => onUndo(step)} disabled={!!busy} className="inline-flex items-center gap-0.5 text-muted-foreground underline-offset-2 hover:underline" data-testid={`${testid}-step-${i}-undo`}><ArrowUUpLeft className="h-3 w-3" /> Undo</button>}
          </p>
        ) : <p className="text-[11px] text-red-600/80 dark:text-red-400/80">Photo required to confirm</p>}
      </div>
    </li>
  );
}

/* Engineer's step checklist on an emergency job: make-safe steps first, then the follow-up repair steps. Every tick needs an
   evidence photo (camera opens on tap) — the client sees the step, the time and the photo. Progress only — never blocks completion. */
export default function MakeSafeChecklist({ jobId, makeSafe, onChange, readOnly = false, testid = "make-safe" }) {
  const [busy, setBusy] = useState("");
  const [pending, setPending] = useState(null);
  const fileRef = useRef(null);
  const steps = makeSafe?.steps || [];
  const repair = makeSafe?.repair || { steps: [], done: 0, total: 0 };
  if (!steps.length && !repair.steps.length) return null;
  const allSafe = steps.length > 0 && makeSafe.done === makeSafe.total;
  const allFixed = repair.total > 0 && repair.done === repair.total;

  const post = async (body, isForm) => {
    const { data } = await API.post(`/in-house-portal/emergency_job/${jobId}/make-safe`, body, isForm ? { headers: { "Content-Type": "multipart/form-data" } } : undefined);
    onChange?.(data);
    return data;
  };

  const startTick = (phase) => (step) => {
    if (readOnly || busy) return;
    setPending({ step, phase });
    fileRef.current?.click();
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const p = pending;
    setPending(null);
    if (!file || !p) return;
    setBusy(p.step.key);
    try {
      const photo = await compressImage(file);
      const fd = new FormData();
      fd.append("key", p.step.key);
      fd.append("phase", p.phase);
      fd.append("done", "true");
      fd.append("file", photo, photo.name || file.name || "step.jpg");
      const data = await post(fd, true);
      if (p.phase === "make_safe" && data.done === data.total) toast.success("All make-safe steps confirmed — the office and the client have been told");
      if (p.phase === "repair" && data.repair?.done === data.repair?.total) toast.success("All repair steps confirmed — the client can see the fix is done");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const undo = (phase) => async (step) => {
    if (readOnly || busy) return;
    setBusy(step.key);
    try { await post({ key: step.key, phase, done: false }); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid={testid} data-done={makeSafe.done} data-total={makeSafe.total}>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid={`${testid}-photo-input`} />
      {makeSafe.intro && <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed" data-testid={`${testid}-intro`}>{makeSafe.intro}</p>}
      {steps.length > 0 && (
        <div className={`bg-card border rounded-lg p-5 border-l-4 ${allSafe ? "border-l-emerald-500 border-emerald-200 dark:border-emerald-900" : "border-l-red-500 border-border"}`} data-testid={`${testid}-phase-make-safe`}>
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5" weight="fill" /> Make safe first</h2>
              <p className="text-xs text-muted-foreground">Tap a step to take its evidence photo — that confirms it. The client sees each photo.</p>
            </div>
            <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${allSafe ? "bg-emerald-600 text-white" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`} data-testid={`${testid}-progress`}>
              {allSafe ? `Made safe ${when(makeSafe.made_safe_at)}` : `${makeSafe.done}/${makeSafe.total} confirmed`}
            </span>
          </div>
          <ul className="space-y-1.5">
            {steps.map((s, i) => <StepRow key={s.key} step={s} i={i} busy={busy} readOnly={readOnly} onTick={startTick("make_safe")} onUndo={undo("make_safe")} testid={testid} />)}
          </ul>
        </div>
      )}
      {repair.steps.length > 0 && (
        <div className={`bg-card border rounded-lg p-5 border-l-4 ${allFixed ? "border-l-emerald-500 border-emerald-200 dark:border-emerald-900" : "border-l-primary border-border"}`} data-testid={`${testid}-phase-repair`}>
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2"><Wrench className="h-5 w-5" weight="fill" /> Then: repair</h2>
              <p className="text-xs text-muted-foreground">Tick each repair step with a photo as you go — the client watches the fix progress.</p>
            </div>
            <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${allFixed ? "bg-emerald-600 text-white" : "bg-primary/15 text-primary"}`} data-testid={`${testid}-repair-progress`}>
              {allFixed ? `Repair complete ${when(repair.completed_at)}` : `${repair.done}/${repair.total} done`}
            </span>
          </div>
          <ul className="space-y-1.5">
            {repair.steps.map((s, i) => <StepRow key={s.key} step={s} i={i} busy={busy} readOnly={readOnly} onTick={startTick("repair")} onUndo={undo("repair")} testid={`${testid}-repair`} />)}
          </ul>
        </div>
      )}
    </div>
  );
}
